import React, { useState } from 'react';

import { useSnackbar } from 'notistack';

import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';

import { ACCOUNT_STATUS } from '~/consts/accountTypes';

import { useSetAccountEnabled } from '~/hooks/apis/users/useSetAccountEnabled';
import useCurrentUser from '~/hooks/useCurrentUser';

import { canEnableDisableAccounts } from '~/utils/adminPermission';

import { getErrorMessage } from './utils';

const ENABLED_OPTION = 'enabled';
const DISABLED_OPTION = 'disabled';

type Props = {
  accountId: string;
  accountEnabled: boolean;
  // Roles and vendors are only set when they changed, which happens when the account is disabled.
  onAccountStatusUpdated: (update: { status: string; roles?: Array<string>; vendors?: Array<string> }) => void;
};

export default function AccountStatusField({ accountId, accountEnabled, onAccountStatusUpdated }: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const { user: currentUser } = useCurrentUser();
  const [pendingEnabled, setPendingEnabled] = useState<boolean | null>(null);
  const setAccountEnabledMutation = useSetAccountEnabled(accountId);

  const hasPermission = canEnableDisableAccounts(currentUser);
  const isDialogOpen = pendingEnabled !== null;

  const handleConfirm = async () => {
    if (pendingEnabled === null) {
      return;
    }

    try {
      const updatedAccount = await setAccountEnabledMutation.mutateAsync(pendingEnabled);
      const isEnabled = updatedAccount?.is_enabled ?? pendingEnabled;
      onAccountStatusUpdated({
        status: isEnabled ? ACCOUNT_STATUS.ENABLED : ACCOUNT_STATUS.DISABLED,
        roles: updatedAccount.roles,
        vendors: updatedAccount.vendors,
      });
      setPendingEnabled(null);
      enqueueSnackbar(isEnabled ? 'Account enabled' : 'Account disabled', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(`Unable to update account status: ${getErrorMessage(error)}`, { variant: 'error' });
    }
  };

  return (
    <Box>
      <TextField
        select
        label="Account Status"
        value={accountEnabled ? ENABLED_OPTION : DISABLED_OPTION}
        onChange={(event) => {
          const nextEnabled = event.target.value === ENABLED_OPTION;
          if (nextEnabled !== accountEnabled) {
            setPendingEnabled(nextEnabled);
          }
        }}
        disabled={!hasPermission || setAccountEnabledMutation.isPending}
        helperText={hasPermission ? undefined : 'Requires both the admin-user and role-admin roles.'}
        sx={{ minWidth: 240 }}
      >
        <MenuItem value={ENABLED_OPTION}>Enabled</MenuItem>
        <MenuItem value={DISABLED_OPTION}>Disabled</MenuItem>
      </TextField>

      <Dialog
        open={isDialogOpen}
        onClose={setAccountEnabledMutation.isPending ? undefined : () => setPendingEnabled(null)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>{pendingEnabled ? 'Enable Account' : 'Disable Account'}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" gutterBottom>
            {pendingEnabled
              ? 'Enabling this account will allow it to sign in again.'
              : 'Disabling this account will block it from signing in, end all of its active sessions and remove all of its roles and vendors.'}
          </Typography>
          <Typography variant="body2">Do you want to continue?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingEnabled(null)} disabled={setAccountEnabledMutation.isPending}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleConfirm} disabled={setAccountEnabledMutation.isPending}>
            {pendingEnabled ? 'Enable' : 'Disable'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
